import { useQuery } from '@apollo/client';
import { navigate } from 'gatsby';
import { stringifyUrl } from 'query-string';
import { head } from 'ramda';
import React, { FC, useContext, useEffect } from 'react';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Skeleton from '@ui/atoms/Skeleton';

import IFrameContainer from '@ui/molecules/IFrameContainer';
import RoundedCard from '@ui/molecules/RoundedCard';

import { getCompletedHrtIntakes } from '@helpers/Intakes/intakes';

import { doesNotExist, exists } from '@utils/general';

import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IAuthenticatedMemberWithCompletedIntakes } from '@folx-types/member';

import { IGahtFuAppointmentSelectionStepPageContent } from './GahtFuAppointmentSelectionStep.component';
import GAHT_FU_BOOKING_URL from './gahtFuBookingUrl.gql';

const BookingContainer = styled.div`
	height: 78rem;
`;

enum BookingErrors {
	ERROR_HAS_UPCOMING_APPOINTMENT = 'ERROR_HAS_UPCOMING_APPOINTMENT',
	ERROR_NO_PROVIDER_FOUND = 'ERROR_NO_PROVIDER_FOUND',
	ERROR_NO_PRIOR_GAHT_VISIT = 'ERROR_NO_PRIOR_GAHT_VISIT',
	ERROR_NO_FU_SERVICE_FOUND_IN_CW = 'ERROR_NO_FU_SERVICE_FOUND_IN_CW',
	ERROR_NO_STATE_SERVICE_FOUND_IN_CW = 'ERROR_NO_STATE_SERVICE_FOUND_IN_CW',
}

interface IBookingUrlResponse {
	authenticatedMember: {
		id: string;
		email: string;
		displayName: string;
		gahtFollowupBookingUrl: {
			bookingUrl?: string;
			bookingUrlType?: 'provider' | 'state';
			errors?: BookingErrors[];
		};
	};
}

export const cogsworthUrlQuery = (member: { id: string; name: string; email: string }) => {
	return {
		'answer[name]': member.name,
		'answer[email]': member.email,
		'answer[text_1]': new URL(`/public/chart/${member.id}`, location.href).href,
		'answer[text_2]': new URL(`/followup`, location.href).href,
		// This is set by the backend, but we have to give it a non-empty value here to prevent CW from asking the member to fill it out
		'answer[google-maps-address_1]': '—',
	};
};

const BookingErrorCard = ({ message }: { message: string }) => (
	<RoundedCard bgColor="cardRed">
		<BasicText>{message}</BasicText>
	</RoundedCard>
);

const GahtFuAppointmentBooking: FC<{
	loading?: boolean;
	errorMessages: IGahtFuAppointmentSelectionStepPageContent['errorMessages'];
	onBookingUrlError?: () => void;
}> = ({ loading = false, errorMessages, onBookingUrlError }) => {
	const { client: graphQLv2Client } = useContext(GraphQLv2Context);

	const {
		loading: gahtFuBookingUrlLoading,
		data: gahtFollowupBookingUrl,
		error: gahtFollowupBookingUrlError,
	} = useQuery<IBookingUrlResponse & IAuthenticatedMemberWithCompletedIntakes>(
		GAHT_FU_BOOKING_URL,
		{
			client: graphQLv2Client,
			errorPolicy: 'all',
		}
	);

	const bookingUrl =
		gahtFollowupBookingUrl?.authenticatedMember?.gahtFollowupBookingUrl?.bookingUrl ?? '';
	const email = gahtFollowupBookingUrl?.authenticatedMember?.email ?? '';
	const displayName = gahtFollowupBookingUrl?.authenticatedMember?.displayName ?? '';
	const memberUuid = gahtFollowupBookingUrl?.authenticatedMember?.id ?? '';
	const errors = gahtFollowupBookingUrl?.authenticatedMember?.gahtFollowupBookingUrl?.errors;

	useEffect(() => {
		if (!bookingUrl && exists(errors)) onBookingUrlError?.();
	}, [bookingUrl, errors, onBookingUrlError]);

	if (
		!bookingUrl &&
		(exists(errors) || exists(gahtFollowupBookingUrlError)) &&
		!(errors?.length === 1 && head(errors) === BookingErrors.ERROR_NO_PROVIDER_FOUND)
	) {
		if (errors?.includes(BookingErrors.ERROR_NO_STATE_SERVICE_FOUND_IN_CW)) {
			return <BookingErrorCard message={errorMessages.stateServiceNotFound} />;
		}
		if (errors?.includes(BookingErrors.ERROR_NO_PRIOR_GAHT_VISIT)) {
			navigate('/app/member/appointments', {
				state: {
					noInitalAppointmentError: true,
					serviceType: getCompletedHrtIntakes(gahtFollowupBookingUrl?.authenticatedMember),
				},
			});
			return <BookingErrorCard message={errorMessages.noPriorGahtVisit} />;
		}
		if (errors?.includes(BookingErrors.ERROR_HAS_UPCOMING_APPOINTMENT)) {
			return <BookingErrorCard message={errorMessages.hasUpcomingAppointment} />;
		}
		return <BookingErrorCard message={errorMessages.genericMessage} />;
	}

	if (gahtFuBookingUrlLoading || !gahtFollowupBookingUrl || loading) {
		return (
			<BookingContainer>
				<Skeleton fullHeight fullWidth structure="single" />
			</BookingContainer>
		);
	}

	const cogsworthUrl = stringifyUrl({
		url: bookingUrl,
		query: cogsworthUrlQuery({ id: memberUuid, name: displayName, email: email }),
	});

	return (
		<BookingContainer>
			<IFrameContainer iFrameStyle={{ height: '100%', width: '100%' }} url={cogsworthUrl} />
		</BookingContainer>
	);
};

export default GahtFuAppointmentBooking;
